import {
  type FormulaTraitConfig,
  type TraitCondition,
  type TraitConfig,
  TraitType,
  isCatalogRecommendationTraitConfig,
  isVariableResultingTypeTraitConfig,
} from "@hightouch/lib/query/visual/types/trait-definitions";
import { useToast } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";

import type { TraitQuery } from "src/graphql";
import { QueryType } from "src/types/models";
import { useModelRun } from "src/utils/models";
import type {
  AndOrCondition,
  PropertyCondition,
  RelatedColumn,
  TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import { TRAIT_PREVIEW_ALIAS } from "@hightouch/lib/query/visual/util";
import { RAW_SQL_JSON_ARR_RES_TYPES } from "./utils";

type AudienceSubset = {
  subset_value: {
    id: string;
  };
};

// Trait types whose preview compiles the trait config inline into the query.
// Tracked-audience-membership traits are excluded: their configuration lives in
// tracked_audience_lists table rather than trait_definitions.config; these are not inlinable
export type InlinePreviewableTraitType = Exclude<
  TraitType,
  TraitType.TrackedAudienceMembership
>;

export type PreviewableTrait =
  | {
      type: InlinePreviewableTraitType;
      config: TraitConfig;
      relationshipId?: string | null;
      conditions?: TraitCondition[];
    }
  | {
      type: TraitType.TrackedAudienceMembership;
      config: TraitConfig;
      conditions?: TraitCondition[];
      traitDefinitionId: string;
    };

export const usePreviewTrait = ({
  isFastPreviewEnabled,
  parentModel,
  trait,
  additionalConditions = [],
  subsets,
}: {
  isFastPreviewEnabled: boolean;
  parentModel: NonNullable<TraitQuery["trait_definitions_by_pk"]>["config"];
  trait: PreviewableTrait;
  additionalConditions?: AndOrCondition<PropertyCondition>[];
  subsets?: AudienceSubset[];
}) => {
  const { toast } = useToast();

  let sortable = true;
  // For RawSql and Formula traits, we can't sort by JSON array result types since they're not
  // comparable values. Need config to be typed as RawSqlTraitConfig | FormulaTraitConfig
  const config = trait.config;
  if (isVariableResultingTypeTraitConfig(trait.type, config)) {
    sortable = !RAW_SQL_JSON_ARR_RES_TYPES.some(
      (type) => type.value === config.resultingType,
    );
  }

  // List traits are not sortable in preview
  // TODO - merge this into the above isVariableResultingTypeTrait check
  if (trait.type === TraitType.List) {
    sortable = false;
  }

  // Tracked-audience-membership traits aggregate to an array per user, which
  // isn't a sortable scalar (same as List).
  if (trait.type === TraitType.TrackedAudienceMembership) {
    sortable = false;
  }

  // Catalog recommendation traits preview as an ordered list of recommended
  // items, so they're aggregated as arrays (not sortable scalars).
  const isCatalogRecommendationTrait = isCatalogRecommendationTraitConfig(
    trait.type,
    trait.config,
  );
  if (isCatalogRecommendationTrait) {
    sortable = false;
  }

  const traitColumn: RelatedColumn | TransformedColumn =
    // Tracked-audience-membership traits have no relationship; reference the
    // saved trait definition directly with an empty join path so the backend
    // reads the audience_membership changelog.
    trait.type === TraitType.TrackedAudienceMembership
      ? {
          type: "related",
          column: {
            type: "trait",
            traitDefinitionId: trait.traitDefinitionId,
            conditions: [],
          },
          path: [],
        }
      : trait.type !== TraitType.Formula && trait.relationshipId
        ? {
            type: "related",
            column: {
              type: "inline_trait",
              traitType: trait.type,
              traitConfig: trait.config,
              conditions: trait.conditions ?? [],
              relationshipId: trait.relationshipId,
              ...(isCatalogRecommendationTrait ? { preview: true } : {}),
            },
            path: [trait.relationshipId],
          }
        : {
            type: "transformed",
            column: {
              type: "inline_trait",
              // Formula traits do not have relationship ids
              traitType: trait.type as TraitType.Formula,
              traitConfig: trait.config as FormulaTraitConfig,
            },
          };

  const visualQueryFilter = {
    conditions: additionalConditions,
    additionalColumns: [
      {
        alias: TRAIT_PREVIEW_ALIAS,
        column: traitColumn,
      },
    ],
  };

  if (sortable) {
    visualQueryFilter["sizeCap"] = {
      limit: 100,
      orderByProperty: traitColumn,
      bottom: false,
    };
  } else {
    visualQueryFilter["sizeCap"] = {
      limit: 100,
      orderByProperty: traitColumn,
      onlySortByNull: true, // if trait isn't sortable, we still want non-NULL values first
    };
  }

  const {
    runQuery,
    columns,
    rows,
    loading,
    error,
    isSuccess,
    inferredPrimaryKey,
  } = useModelRun(
    {
      query_type: QueryType.Visual,
      visual_query_parent_id: parentModel?.id,
      connection: parentModel?.connection,
      visual_query_filter: visualQueryFilter,
      subsets,
    },
    {
      columns: parentModel?.columns,
      includeTraitDependencies: true,
      useDefaultSubsets: !subsets?.length,
      useSampledModels: isFastPreviewEnabled,
      onCompleted: (_data, error) => {
        if (error) {
          toast({
            id: "trait-preview",
            title: "Trait preview failed",
            variant: "error",
          });

          captureException(error);
          return;
        }
      },
    },
  );

  const previewTrait = async () => {
    await runQuery({ limit: true, disableRowCounter: true });
  };

  return {
    previewTrait,
    isLoading: loading,
    data: { rows, columns, inferredPrimaryKey },
    error,
    isSuccess,
  };
};
